import { AxiosError } from "axios";
import apiClient from "./api-client";
import {
  isSessionExpiredPayload,
  redirectToLoginOnSessionExpired,
} from "@/utils/auth-session";
import { store } from "@/redux/store";
import { logout } from "@/redux/features/auth-slice";
import { streamRequest, type StreamOutcome } from "./sse-stream";

export { isGatewayError, StreamIdleTimeoutError, type StreamOutcome } from "./sse-stream";

function isDirectBackendUrl(endpoint: string): boolean {
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/$/, "");
  if (!baseUrl) return false;
  return endpoint === baseUrl || endpoint.startsWith(`${baseUrl}/`);
}

/** Upload files to any endpoint via multipart/form-data. Files can be File or Blob. Returns response.data. */
export const uploadFiles = async <T = unknown>(
  endpoint: string,
  files: (File | Blob)[],
  options?: { fileKey?: string }
): Promise<T> => {
  const formData = new FormData();
  const key = options?.fileKey ?? "files";
  files.forEach((file) => formData.append(key, file instanceof File ? file : file, file instanceof File ? file.name : "file"));

  if (isDirectBackendUrl(endpoint)) {
    const token = await getDirectUploadAccessToken();
    const response = await apiClient.post<T>(backendApiUrl(endpoint), formData, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return response.data;
  }

  const response = await apiClient.post<T>(endpoint, formData);
  return response.data;
};

async function getDirectUploadAccessToken(): Promise<string | null> {
  const response = await apiClient.post<{ access_token: string }>("/api/auth/upload-bearer");
  return response.data.access_token;
}

function backendApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_API_BASE_URL is not configured");
  }
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

/** POST FormData straight to the backend API with the same cookie/refresh flow as /api/[...path]. */
export const postBackendForm = async <T = any>(path: string, formData: FormData) => {
  return sendBackendForm<T>("post", path, formData);
};

/**
 * Send FormData straight to the backend API, skipping the /api/[...path] proxy
 * (Vercel caps request bodies at ~4.5 MB). Reports upload progress as a percent.
 */
export const sendBackendForm = async <T = unknown>(
  method: "post" | "patch",
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void
) => {
  const token = await getDirectUploadAccessToken();
  return apiClient.request<T>({
    method,
    url: backendApiUrl(path),
    data: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    onUploadProgress: (event) => {
      if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100));
    },
  });
};

export const uploadFilesToBackend = async <T = unknown>(
  path: string,
  files: (File | Blob)[],
  options?: { fileKey?: string }
): Promise<T> => {
  const formData = new FormData();
  const key = options?.fileKey ?? "files";
  files.forEach((file) =>
    formData.append(
      key,
      file instanceof File ? file : file,
      file instanceof File ? file.name : "file"
    )
  );
  const response = await postBackendForm(path, formData);
  return response.data as T;
};

export const fetchData = async (endpoint: string) => {
  try {
    const response = await apiClient.get(endpoint);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};

export const post = async (url: string, data?: unknown) => {
  try {
    const response = await apiClient.post(url, data);
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};

export const put = (url: string, data: unknown) => {
  return apiClient.put(url, data);
};

export const del = (url: string, data?: unknown) => {
  return apiClient.delete(url, { data });
};

export const patch = async (url: string, data: unknown) => {
  try {
    const response = await apiClient.patch(url, data);
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};



export const fetchCsv = async (
  endpoint: string
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await apiClient.get(endpoint, {
      responseType: "blob",
    });
    // Try to get filename from Content-Disposition
    let filename = "Persons.csv";
    const disposition = response.headers["content-disposition"];
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match && match[1]) {
        filename = match[1].replace(/['\"]/g, "").trim();
      }
    }
    return { blob: response.data, filename };
  } catch (error) {
    throw new Error(`Error fetching CSV: ${error}`);
  }
};

const getFriendlyStatusMessage = (status: number): string => {
  switch (status) {
    case 400: return "Bad request. Please check your input and try again.";
    case 401: return "Session expired. Please log in again.";
    case 403: return "You don't have permission to perform this action.";
    case 404: return "We couldn't find what you were looking for. It may have been deleted or moved.";
    case 408: return "The request timed out. Please try again.";
    case 409: return "This action conflicts with the current state. Please refresh and try again.";
    case 413: return "The file or message is too large. Please try something smaller.";
    case 422: return "Some of the information provided isn't valid. Please review and try again.";
    case 429: return "You're sending requests too quickly. Please wait a moment and try again.";
    case 500: return "Something went wrong on our end. Please try again.";
    case 502:
    case 503:
    case 504: return "The server is temporarily unavailable. Please try again in a moment.";
    default:
      return status >= 500
        ? "Something went wrong on our end. Please try again."
        : "We couldn't complete your request. Please try again.";
  }
};

const buildErrorFromResponse = async (response: Response): Promise<Error & { status: number }> => {
  let serverMessage: string | null = null;
  try {
    const text = await response.text();
    if (text) {
      try {
        const json = JSON.parse(text);
        const candidate = json?.message ?? json?.detail ?? json?.error;
        if (typeof candidate === "string" && candidate.trim()) {
          serverMessage = candidate.trim();
        } else if (Array.isArray(json?.detail) && typeof json.detail[0]?.msg === "string") {
          serverMessage = json.detail[0].msg;
        }
      } catch {
        if (text.trim() && text.length < 300) serverMessage = text.trim();
      }
    }
  } catch {
    // body not readable — fall through to status-based message
  }
  return Object.assign(new Error(serverMessage || getFriendlyStatusMessage(response.status)), {
    status: response.status,
  });
};

const handleFetchAuthFailure = async (response: Response): Promise<never> => {
  let data: { error_code?: string; message?: string; detail?: string } | undefined;
  try {
    data = await response.clone().json();
  } catch {
    data = undefined;
  }

  if (isSessionExpiredPayload(data, response.headers)) {
    if (typeof window !== "undefined") {
      store.dispatch(logout());
      redirectToLoginOnSessionExpired();
    }
    throw new Error(
      data?.message?.trim() || "Session expired. Please log in again.",
    );
  }

  throw await buildErrorFromResponse(response);
};

const checkStreamResponse = async (response: Response): Promise<void> => {
  if (response.status === 401) {
    await handleFetchAuthFailure(response);
  }
  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }
};

/**
 * Both stream helpers resolve with how the stream ended (clean close, caller abort or a dropped
 * connection) and reject on HTTP errors (with `status`) or the idle timeout (StreamIdleTimeoutError).
 */
export const getStream = (
  url: string,
  onChunk: (chunk: string) => void,
  externalSignal?: AbortSignal,
  onActivity?: () => void
): Promise<StreamOutcome> =>
  streamRequest({ url, method: "GET", onChunk, signal: externalSignal, checkResponse: checkStreamResponse, onActivity });

export const postStream = (
  url: string,
  data: unknown,
  onChunk: (chunk: string) => void,
  externalSignal?: AbortSignal,
  onActivity?: () => void
): Promise<StreamOutcome> =>
  streamRequest({
    url,
    method: "POST",
    body: data,
    onChunk,
    signal: externalSignal,
    checkResponse: checkStreamResponse,
    onActivity,
  });
